/* ============================================================
   ZNAKI · audio/banks.js
   Dobór próbek dla figury — banki, grupy wysokości i warianty.

   Zasada nadrzędna (tiki): bank jest przypisany ŚCIŚLE do kształtu
   i koloru figury. Tik czarnego prostokąta nigdy nie zagra na
   niczym innym niż czarny prostokąt. Figura bez własnego banku
   (koła, białe i szarobrązowe prostokąty) po prostu milczy.

   Wysokości (zamysł autora dźwięku):
     · katalog tików dzieli się na grupy A, B, C… różniące się
       wysokością — kolejna kreska ma czerpać z INNEJ grupy,
       żeby sąsiednie figury grały wyraźnie inne dźwięki;
     · wewnątrz grupy (A1, A2, A3…) warianty różnią się ledwie
       słyszalnie i mają lecieć w kolejności losowej, bez
       wyczuwalnego zapętlenia.

   Stąd: kursor grupy krąży po banku (round-robin) i rozdaje
   kolejnym figurom kolejne grupy, a każda figura tasuje sobie
   własną kolejność wariantów.

   Wyjątkiem jest bank glitcha (nextGlitch): nie należy do żadnej
   figury — próbki krążą wspólną, tasowaną kolejką, bo rozdarcie
   sygnału ma za każdym razem brzmieć inaczej.

   Drugim wyjątkiem jest bank fal (nextFala) — i to wyjątek odwrotny:
   tu jako jedyny NIE wolno niczego tasować. Fale są barwowo podobne,
   ale mają różne wysokości i idą po kolei, plik po pliku, przez kolejne
   pojawiające się figury. Z tej kolejności bierze się przewijająca się
   przez instalację sugestia melodii — zamysł autora dźwięku. Kursor
   rusza dopiero wtedy, gdy fala naprawdę zabrzmi (accents.js pyta
   o plik po limitach), żeby figury odrzucone nie robiły dziur w linii.
   ============================================================ */

import { mulberry32 } from '../utils';

/* kształty, które falują: patyczki i prostokąty (zamysł autora dźwięku).
   Kropki i kliny bywają powolne, ale fali nie dostają. */
const FALA_SHAPES = { line: true, bar: true };

/* kształt + kolor figury → klucz banku tików (null = figura milczy) */
export const tikiBankKey = (fig) => {
  if (fig.shape === 'line') {
    if (fig.color === 'black') return 'line_black';
    if (fig.color === 'white') return 'line_white';
    return null;
  }
  if (fig.shape === 'bar') {
    if (fig.color === 'black')  return 'bar_black';
    if (fig.color === 'orange') return 'bar_orange';
    return null;                      /* białe smugi i widmowe panele milczą */
  }
  if (fig.shape === 'tri') {
    if (fig.color === 'white') return 'tri_white';
    if (fig.color === 'taupe') return 'tri_taupe';
    return null;
  }
  return null;                        /* koła — bez tików */
};

class Banks {

  /**
   * @param {object} data — sekcja "audio" z data-playground-params
   */
  constructor(data) {
    this.tiki   = (data && data.tiki) || {};
    this.exit   = (data && data.exit) || {};
    this.zegary = (data && data.zegary) || [];
    this.glitch = (data && data.glitch) || [];
    this.fale   = (data && data.fale) || [];

    /* kursor grup per bank — kolejne figury dostają kolejne wysokości */
    this._cursor = new Map();

    /* potasowana kolejka próbek glitcha — patrz nextGlitch() */
    this._glOrder = [];
    this._glIdx = 0;
    this._glLast = null;

    /* kursor fal — jedzie po plikach po kolei, patrz nextFala() */
    this._falaIdx = 0;
  }

  /** wszystkie adresy próbek akcydentalnych — do wstępnego wczytania */
  allUrls() {
    const out = [];
    for (const bank of Object.values(this.tiki)) {
      for (const group of Object.values(bank)) out.push(...group);
    }
    for (const family of Object.values(this.exit)) out.push(...family);
    out.push(...this.zegary);
    out.push(...this.glitch);
    out.push(...this.fale);
    return out;
  }

  /**
   * Przydział banku tików dla figury: grupa wysokości + potasowana
   * kolejność wariantów. Zwraca null, gdy figura nie ma swojego banku.
   * @param {object} fig
   * @return {?object} { key, group, order }
   */
  assignTiki(fig) {
    const key = tikiBankKey(fig);
    if (!key) return null;
    const bank = this.tiki[key];
    if (!bank) return null;

    const groups = Object.keys(bank).filter((g) => bank[g] && bank[g].length);
    if (!groups.length) return null;

    /* kolejna figura tego banku → kolejna grupa wysokości */
    const at = this._cursor.get(key) || 0;
    this._cursor.set(key, (at + 1) % groups.length);
    const group = groups[at % groups.length];

    return { key, group, order: shuffle(bank[group], mulberry32(fig.seed)) };
  }

  /**
   * "Zasysanie" przy znikaniu figury. Szybkie wyjścia dostają krótsze
   * próbki (ex_k), leniwe — dłuższe (ex_d).
   * @param {object} fig
   * @return {?String[]} potasowana kolejność wariantów
   */
  assignExit(fig) {
    const long = this.exit.long || [];
    const short = this.exit.short || [];
    const family = (fig.outDur >= 0.6 ? long : short);
    const pool = family.length ? family : (long.length ? long : short);
    if (!pool.length) return null;
    return shuffle(pool, mulberry32(fig.seed + 977));
  }

  /** pętla zegara dla figury obracającej się równomiernie */
  assignZegar(fig) {
    if (!this.zegary.length) return null;
    return this.zegary[Math.abs(fig.seed) % this.zegary.length];
  }

  /**
   * Kolejna próbka rozdarcia sygnału. W odróżnieniu od pozostałych banków
   * NIE jest przypisana do figury: glitch ma zaskakiwać, więc ta sama
   * tarcza za każdym razem rozdziera się inaczej. Świadomie liczone na
   * Math.random — tak jak samo wyzwolenie glitcha (figures.js).
   * Kolejka jest tasowana, a nie losowana za każdym razem, żeby przy
   * pięciu próbkach nie wychodziły serie tego samego dźwięku.
   * @return {?String}
   */
  nextGlitch() {
    if (!this.glitch.length) return null;
    if (this._glIdx >= this._glOrder.length) {
      this._glOrder = shuffle(this.glitch, Math.random);
      /* bez powtórki na styku dwóch kolejek */
      if (this._glOrder.length > 1 && this._glOrder[0] === this._glLast) {
        const t = this._glOrder[0]; this._glOrder[0] = this._glOrder[1]; this._glOrder[1] = t;
      }
      this._glIdx = 0;
    }
    this._glLast = this._glOrder[this._glIdx++];
    return this._glLast;
  }

  /**
   * Czy figura w ogóle ma czym falować. Pytanie bez skutków ubocznych —
   * accents.js sprawdza to zanim postawi figurze łańcuch, żeby powolne
   * kropki i kliny nie zajmowały węzłów Web Audio na dźwięk, którego
   * i tak nigdy nie dostaną.
   * @param {object} fig
   * @return {Boolean}
   */
  canWave(fig) {
    return !!(this.fale.length && FALA_SHAPES[fig.shape]);
  }

  /**
   * Kolejna fala — ŚCIŚLE w kolejności plików (fala_1, fala_2, …),
   * bez tasowania i bez losowania: to ta kolejność niesie melodię.
   * Kursor jest wspólny dla całej instalacji, więc kolejne figury
   * dostają kolejne wysokości.
   * @param {object} fig
   * @return {?String} null, gdy kształt figury nie faluje
   */
  nextFala(fig) {
    if (!this.canWave(fig)) return null;
    const url = this.fale[this._falaIdx % this.fale.length];
    this._falaIdx = (this._falaIdx + 1) % this.fale.length;
    return url;
  }
}

/* tasowanie Fishera–Yatesa deterministycznym RNG figury */
function shuffle(arr, rng) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = a[i]; a[i] = a[j]; a[j] = t;
  }
  return a;
}

export default Banks;
